import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import { useEffect, useRef } from 'react';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { isDesktopPreviewPath, workspaceDirectoryStore } from './directory';

/** The core's event that the directory changed: pages read it again. */
export const WORKSPACES_CHANGED_EVENT = 'crowdex/workspaces.changed';

/** Changes that arrive together (a project and its conversation) make one read. */
const REFRESH_DELAY_MS = 150;

export interface WorkspaceDirectoryBootstrapOptions {
  /** The shell's preview: the store holds the boards' data, nothing is fetched. */
  preview?: boolean;
  /** Told of the placements whenever the directory reads them (the sidebar's threads take them). */
  onPlacements?: (placements: Readonly<Record<string, ConversationPlacement>>) => void;
}

/**
 * Loads the workspace directory when the shell starts, and again whenever the server says it
 * changed (`crowdex/workspaces.changed`). The shell calls it once.
 */
export function useWorkspaceDirectoryBootstrap({
  preview = isDesktopPreviewPath(),
  onPlacements,
}: WorkspaceDirectoryBootstrapOptions = {}): void {
  const placementsListener = useRef(onPlacements);
  placementsListener.current = onPlacements;

  useEffect(() => {
    const store = workspaceDirectoryStore();
    const unsubscribe = store.subscribe((state, previous) => {
      if (state.placements !== previous.placements) placementsListener.current?.(state.placements);
    });
    if (preview) return unsubscribe;
    void store.getState().refresh();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stopListening = onStreamedEvent((event) => {
      if (event.type !== WORKSPACES_CHANGED_EVENT) return;
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void store.getState().refresh();
      }, REFRESH_DELAY_MS);
    });
    return () => {
      unsubscribe();
      stopListening();
      if (timer !== null) clearTimeout(timer);
    };
  }, [preview]);
}

/**
 * The sidebar's threads with the directory's placements: a thread of this server takes its
 * entry (none: it sits nowhere now); one on the linked Cloud keeps what the Cloud said. The
 * same array when nothing changed.
 */
export function withPlacements<
  T extends { id: string; onCloud?: true; placement?: ConversationPlacement },
>(
  threads: readonly T[],
  placements: Readonly<Record<string, ConversationPlacement>>,
): readonly T[] {
  let changed = false;
  const next = threads.map((thread) => {
    if (thread.onCloud === true) return thread;
    const placement = placements[thread.id];
    // The directory lists only conversations that sit somewhere: nowhere is no entry.
    const current =
      thread.placement === undefined ||
      Object.values(thread.placement).every((value) => value === null)
        ? undefined
        : thread.placement;
    if (JSON.stringify(placement) === JSON.stringify(current)) return thread;
    changed = true;
    const { placement: _previous, ...rest } = thread;
    return (placement === undefined ? rest : { ...rest, placement }) as T;
  });
  return changed ? next : threads;
}
